import { useMemo } from "react";
import ReportRateCard from "../../../../../mouse-management/src/pages/performance/ReportRateCard";
import BatteryLifeCard from "../../../../../mouse-management/src/pages/performance/BatteryLifeCard";
import SensorModeCard from "../../../../../mouse-management/src/pages/performance/SensorModeCard";
import WorkModeCard from "../../../../../mouse-management/src/pages/performance/WorkModeCard";
import ZhanliPerformanceModeCard from "../../../../../mouse-management/src/pages/performance/zhanli/ZhanliPerformanceModeCard";
import ZhanliOverclockSwitch from "../../../../../mouse-management/src/pages/performance/zhanli/ZhanliOverclockSwitch";
import ZhanliOverclockArena from "../../../../../mouse-management/src/pages/performance/zhanli/ZhanliOverclockArena";
import { ZHANLI_SENSOR_MODE_20K_FPS } from "../../../../../mouse-management/src/pages/performance/zhanli/zhanliPerformanceMode";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import { ProductImage } from "../../ProductImage";
import {
  getMousePerformanceValues,
  getZhanliPerformancePreviewValues,
} from "../utils/mouseConfigPreviewUtils";
import "../../../../../mouse-management/src/pages/performance/index.scss";
import "../../../../../mouse-management/src/pages/performance/SensorModeCard.scss";
import "../../../../../mouse-management/src/pages/performance/PerformanceMouse.scss";

const REPORT_RATES = [
  { value: 0, rate: 125, option: "125" },
  { value: 1, rate: 250, option: "250" },
  { value: 2, rate: 500, option: "500" },
  { value: 3, rate: 1000, option: "1K" },
  { value: 4, rate: 2000, option: "2K" },
  { value: 5, rate: 4000, option: "4K" },
  { value: 6, rate: 8000, option: "8K" },
];

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

export default function MousePerformanceSection({ meta, configData }: Props) {
  const provider =
    meta.solutionProvider === "freq"
      ? "freq"
      : meta.solutionProvider === "beiying"
        ? "beiying"
        : meta.solutionProvider === "zhanli"
          ? "zhanli"
          : "jiangmeng";

  const perf = useMemo(
    () => getMousePerformanceValues(configData),
    [configData]
  );
  const zhanliPerf = useMemo(
    () => getZhanliPerformancePreviewValues(configData),
    [configData]
  );

  const reportRateIndex =
    provider === "zhanli" ? zhanliPerf.reportRate : perf.reportRate;
  const reportRateHz =
    REPORT_RATES.find((r) => r.value === reportRateIndex)?.rate ?? 125;
  const isBeiying = provider === "beiying";
  const isZhanli = provider === "zhanli";
  const isOverclock =
    isZhanli && zhanliPerf.protocolMode === ZHANLI_SENSOR_MODE_20K_FPS;

  if (isOverclock) {
    return (
      <div className="community-mouse-performance-preview performance-page performance-page--overclock">
        <div className="mouse-content community-mouse-performance-overclock">
          <ZhanliOverclockArena active reportRateHz={reportRateHz || 8000} />
          <div className="performance-left performance-left--zhanli">
            <ZhanliOverclockSwitch
              checked
              disabled
              onChange={() => undefined}
            />
            <div className="performance-mouse-container">
              <div className="mouse-display">
                <ProductImage
                  displayname={meta.displayname || meta.mouseDeviceId}
                  alt={meta.displayname || "Mouse"}
                  className="mouse-model"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="community-mouse-performance-preview performance-page">
      <div className="performance-right">
        <ReportRateCard
          reportRate={reportRateIndex}
          reportRates={
            provider === "freq" ? REPORT_RATES.slice(0, 4) : REPORT_RATES
          }
          onReportRateChange={() => undefined}
          maxReportRateHz={provider === "freq" ? 1000 : 8000}
        />
        <BatteryLifeCard
          reportRate={reportRateHz}
          sensorMode={isBeiying || isZhanli ? 0 : perf.sensorMode}
          solutionProvider={provider}
          deviceId={meta.mouseDeviceId}
          zhanliProtocolMode={isZhanli ? zhanliPerf.protocolMode : undefined}
        />
        {provider === "freq" ? null : isBeiying ? (
          <WorkModeCard
            workMode={perf.workMode}
            onWorkModeChange={() => undefined}
          />
        ) : isZhanli ? (
          <ZhanliPerformanceModeCard
            protocolMode={zhanliPerf.protocolMode}
            onModeChange={() => undefined}
            disabled
          />
        ) : (
          <SensorModeCard
            sensorMode={perf.sensorMode}
            onSensorModeChange={() => undefined}
          />
        )}
      </div>
    </div>
  );
}
